import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle, Sparkle, ArrowsClockwise, CreditCard, Warning } from "@phosphor-icons/react";
import { useTenantCtx } from "@/lib/pro";

/**
 * Field Radar Billing / Upgrade page.
 * - Reads the live Stripe catalog (via /api/billing/plans)
 * - Fires /billing/checkout to redirect the user into Stripe Checkout
 * - On return (?session_id=…), polls /billing/status until the plan is live
 * - Opens the Stripe Customer Portal for card / cancel management
 */
export default function BillingSettings() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { tenant } = useTenantCtx();
  const [plans, setPlans] = useState(null);
  const [busyLookup, setBusyLookup] = useState(""); // which button is spinning
  const [openingPortal, setOpeningPortal] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");

  useEffect(() => {
    api.get("/billing/plans").then(r => setPlans(r.data.plans || []));
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    let done = false;
    setConfirming(true);
    let ticks = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          done = true;
          setConfirming(false);
          toast.success(`Welcome to Field Radar · ${data.target_plan_tier?.toUpperCase() || "Pro"} 🎉`);
          setParams({});
          setTimeout(() => window.location.reload(), 800);
          return;
        }
      } catch (e) {
        // 404 briefly right after redirect; retry
      }
      ticks += 1;
      if (ticks > 20) {
        done = true;
        setConfirming(false);
        toast.error("Still processing — refresh in a moment to see your new plan.");
        return;
      }
      if (!done) setTimeout(poll, 1500);
    };
    poll();
    return () => { done = true; };
  }, [sessionId, setParams]);

  useEffect(() => {
    if (cancelled) {
      toast.info("Upgrade cancelled — no charges made.");
      setParams({});
    }
  }, [cancelled, setParams]);

  const upgrade = async (lookup_key) => {
    setBusyLookup(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start upgrade");
      setBusyLookup("");
    }
  };

  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.href.split("?")[0],
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't open billing portal");
      setOpeningPortal(false);
    }
  };

  const currentTier = tenant?.plan_tier || tenant?.plan || "free";
  const currentModules = tenant?.modules || [];
  const currentLookup = tenant?.current_lookup_key || "";  // set by Stripe webhook on paid signups
  const subStatus = tenant?.subscription_status;

  // Match a plan to "current":
  //  1. If Stripe has stamped a specific lookup_key on the tenant, prefer that (differentiates Complete vs Enterprise which share modules).
  //  2. Otherwise, match by module set — grandfathered/free tenants land on Free.
  const modulesMatch = (plan) => {
    const planModules = plan.modules || [];
    if (currentLookup && plan.lookup_key) return currentLookup === plan.lookup_key;
    if (!planModules.length) return currentTier === "free" && !currentModules.length;
    // Enterprise vs Complete share modules — without a lookup key we can't tell them apart,
    // so pick the non-highlighted (Complete Bundle is `highlight:true`) as the default.
    const a = [...planModules].sort().join(",");
    const b = [...currentModules].sort().join(",");
    if (a !== b) return false;
    // Both Complete + Enterprise match on modules; prefer Complete (highlight=true) unless tenant is enterprise tier.
    if (currentTier === "enterprise") return plan.tier === "enterprise";
    return plan.tier !== "enterprise";
  };

  return (
    <AdminShell>
      <div className="p-6 md:p-10 max-w-6xl mx-auto space-y-8">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0f172a]" data-testid="billing-title">
            Billing & Plan
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Manage your Field Radar subscription. You're currently on <b className="uppercase">{currentTier}</b>
            {subStatus === "trial" && (
              <span className="ml-2 inline-block px-2 py-0.5 bg-amber-100 border border-amber-400 text-amber-900 text-[10px] font-black uppercase tracking-widest">
                Trial · {tenant?.subscription_days_left} day{(tenant?.subscription_days_left || 0) === 1 ? "" : "s"} left
              </span>
            )}
            {subStatus === "active" && (
              <span className="ml-2 inline-block px-2 py-0.5 bg-green-100 border border-green-500 text-green-900 text-[10px] font-black uppercase tracking-widest">
                Active
              </span>
            )}
            {subStatus === "past_due" && (
              <span className="ml-2 inline-block px-2 py-0.5 bg-red-100 border border-red-500 text-red-900 text-[10px] font-black uppercase tracking-widest">
                Past Due
              </span>
            )}
          </p>
        </div>

        {confirming && (
          <div className="border-2 border-amber-400 bg-amber-50 p-4 flex items-center gap-3" data-testid="billing-confirming">
            <ArrowsClockwise weight="bold" size={20} className="animate-spin text-amber-600" />
            <span className="font-bold text-sm">Confirming your payment with Stripe…</span>
          </div>
        )}

        {tenant?.stripe_customer_id && (
          <div className="border-2 border-[#0f172a] bg-white p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard weight="bold" size={22} />
              <div>
                <div className="font-black uppercase tracking-wider text-sm">Payment method + invoices</div>
                <div className="text-xs text-slate-500">Change card, download receipts, cancel any time.</div>
              </div>
            </div>
            <button
              onClick={openPortal}
              disabled={openingPortal}
              data-testid="billing-open-portal"
              className="tactile-btn h-10 px-4 bg-[#0f172a] hover:bg-black text-white text-xs font-black uppercase tracking-widest disabled:opacity-60"
            >
              {openingPortal ? "Opening…" : "Manage Billing"}
            </button>
          </div>
        )}

        {!plans && <div className="text-center py-16 font-black text-slate-400 uppercase tracking-widest">Loading plans…</div>}

        {plans && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6" data-testid="billing-plans">
            {plans.map((p) => {
              const isCurrent = modulesMatch(p);
              const isBusy = busyLookup === p.lookup_key;
              const highlight = p.highlight;
              return (
                <div
                  key={p.lookup_key || p.tier}
                  data-testid={`billing-plan-${p.lookup_key || p.tier}`}
                  className={`relative border-2 border-[#0f172a] p-6 flex flex-col ${
                    highlight ? "shadow-[10px_10px_0px_#00C4CC] bg-white -translate-y-1" : "shadow-[6px_6px_0px_#0f172a] bg-white"
                  }`}
                >
                  {highlight && (
                    <div className="absolute -top-3 left-6 px-2.5 py-1 bg-[#00C4CC] text-white text-[10px] font-black uppercase tracking-widest">
                      Best Value · Save $19
                    </div>
                  )}
                  <div className="font-display text-xl font-black uppercase tracking-tight">{p.name}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-4xl font-black">${(p.price_cents / 100).toFixed(0)}</span>
                    <span className="text-sm text-slate-500 font-bold">/ {p.interval}</span>
                  </div>
                  {p.modules?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {p.modules.includes("maintenance") && <span className="px-1.5 py-0.5 bg-cyan-100 border border-cyan-500 text-cyan-900 text-[9px] font-black uppercase tracking-widest">🔧 Maintenance</span>}
                      {p.modules.includes("management") && <span className="px-1.5 py-0.5 bg-fuchsia-100 border border-fuchsia-500 text-fuchsia-900 text-[9px] font-black uppercase tracking-widest">🏘️ Management</span>}
                    </div>
                  )}
                  <ul className="mt-5 space-y-2 flex-1">
                    {p.features.map((f, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <CheckCircle weight="fill" size={16} className="text-[#00C4CC] mt-0.5 flex-shrink-0"/>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6">
                    {isCurrent ? (
                      <button disabled data-testid={`billing-current-${p.lookup_key || p.tier}`}
                        className="w-full h-11 border-2 border-slate-300 text-slate-500 font-black uppercase tracking-widest text-xs cursor-not-allowed">
                        Current Plan
                      </button>
                    ) : p.lookup_key ? (
                      <button onClick={() => upgrade(p.lookup_key)} disabled={isBusy}
                        data-testid={`billing-upgrade-${p.lookup_key}`}
                        className={`tactile-btn w-full h-11 font-black uppercase tracking-widest text-xs disabled:opacity-60 ${
                          highlight ? "bg-[#00C4CC] hover:bg-cyan-500 text-white" : "bg-[#0f172a] hover:bg-black text-white"
                        }`}>
                        {isBusy ? "Redirecting…" : <span className="inline-flex items-center gap-2"><Sparkle weight="fill" size={14}/> {p.name.startsWith("Enterprise") ? "Contact Sales" : "Upgrade"}</span>}
                      </button>
                    ) : (
                      <button disabled className="w-full h-11 border-2 border-slate-300 text-slate-500 font-black uppercase tracking-widest text-xs">
                        Downgrade via portal
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="border-2 border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 flex gap-3">
          <Warning weight="fill" size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <span>
            <b>Test mode.</b> Use card <code className="bg-white px-1.5 py-0.5 border">4242 4242 4242 4242</code> ·
            any future expiry · any CVC · any ZIP to complete an upgrade end-to-end.
            Emergent switches you to live keys automatically after you finish Stripe onboarding.
          </span>
        </div>
      </div>
    </AdminShell>
  );
}
